// vt navigation helpers — port of invoiceTest/src/nav.js onto the real path router.
//
// invoiceTest has no router, so it keeps its own back-stack; the wave app HAS real URLs, but the
// owner-approved behaviours still live here because the browser can't provide them:
//   · the ← button LABELS itself with where you came from ("← Dashboard" vs "← Invoices") and
//     returns there (owner 2026-07-29: back goes where you CAME FROM, not where a document
//     "belongs");
//   · a side-nav jump RESETS the trail to Dashboard (a drawer click is a fresh start);
//   · editors are never a ← target (leaving one was deliberate — returning risks a double-post);
//   · scroll position is remembered PER SCREEN and restored on ← (the scroll container is
//     .shell-main, not the window — nothing visibly jumps);
//   · an editor with unsaved changes registers a leave-guard every navigation funnel consults.
import { navigate, html, signal } from './t4.js';

let stack = [];                       // [{ path, label }]
// Bumped on every change to the trail. A screen builds its ← chip BEFORE its frame (tshell) records the
// move that brought it there, so a label read at build time named the screen one step further back
// while a click went to the screen you came from (owner 2026-10-08: the chip read "Reports" and went
// to Settings). The chip reads its label through this, so it follows the trail once the move is in.
const trailTick = signal(0);
function trailChanged() { trailTick.value = trailTick.value + 1; }
/** Read inside a reactive slot to re-render when the trail changes (see backChip). */
export function watchTrail() { return trailTick.value; }

// A screen can name itself on the trail (e.g. a report: "General Ledger", not its section "Reports").
const screenLabels = new Map();       // path (no query) → label
export function setScreenLabel(path, label) { if (path && label) screenLabels.set(String(path).split('?')[0], label); }
export function screenLabel(path) { return path ? screenLabels.get(String(path).split('?')[0]) || null : null; }
let backNavInFlight = false;          // set by goBack; the next recordNav is an unwind, not a push
let backRestorePending = false;       // true ONLY during the synchronous mount a ← triggers, so a
                                      // screen can restore per-view state (e.g. the list's last tab)
                                      // on back but NOT on a fresh forward nav / drawer click.
export function wasBackNav() { return backRestorePending; }
const MAX_DEPTH = 30;
const lastScroll = new Map();         // path → scrollTop

// Live scroll tracking. The scroll container (.shell-main) resets to 0 the instant a new screen
// mounts, so reading getScroll() at recordNav time (which runs on the NEW mount) always saw 0 and
// back-nav never restored position. Instead track the CURRENT screen's scroll continuously — a
// capture-phase listener catches .shell-main's (non-bubbling) scroll — so when we navigate away
// the leaving screen's real scrollTop is already stored under its path. tshell calls markCurrent
// on every shell mount.
let currentPath = null;
let scrollHooked = false;
function hookScroll() {
  if (scrollHooked || typeof document === 'undefined') return;
  scrollHooked = true;
  document.addEventListener('scroll', () => {
    if (currentPath) lastScroll.set(currentPath, getScroll());
  }, true);
}
export function markCurrent(path) { currentPath = path ? path.split('?')[0] : null; hookScroll(); }

// ── leave guard (unsaved editor) ──────────────────────────────────────────────
let leaveGuard = null;
export function setLeaveGuard(fn) { leaveGuard = fn; }
export function clearLeaveGuard() { leaveGuard = null; }
export function mayLeave(toPath) {
  if (!leaveGuard) return true;
  const ok = leaveGuard(toPath ?? null);
  if (ok) leaveGuard = null;
  return ok;
}

// Screens a ← must never land on (an editor you deliberately left).
const NEVER_RETURN_RE = /\/(new|edit)(\/|$)/;

const scrollHost = () => document.querySelector('.shell-main');
export function getScroll() { const el = scrollHost(); return el ? el.scrollTop : (window.scrollY || 0); }
export function setScroll(y) { const el = scrollHost(); if (el) el.scrollTop = y; else window.scrollTo(0, y); }

/** Record a real move away from `fromPath` (same-path re-renders are not moves). */
export function recordNav(fromPath, fromLabel, toPath) {
  if (!fromPath || !toPath || fromPath === toPath) return;
  // fromPath's scroll is tracked live by hookScroll — do NOT overwrite it here with getScroll(),
  // which reads 0 now that the new screen has already reset .shell-main.
  // A ← move UNWINDS the trail — recording the screen being left would push it straight back on,
  // so the next ← (labelled from build time) would bounce forward again (S16 live find: list chip
  // said "← Dashboard", clicked back to the detail it had just left).
  if (backNavInFlight) { backNavInFlight = false; return; }
  if (NEVER_RETURN_RE.test(fromPath)) return;
  stack.push({ path: fromPath, label: fromLabel });
  if (stack.length > MAX_DEPTH) stack.shift();
  trailChanged();
}

/** A drawer click is a fresh start: trail resets to the company dashboard. */
export function resetNavToHome(companyId, toPath) {
  backNavInFlight = false;
  stack = [];
  const dash = '/co/' + companyId + '/dashboard';
  if (toPath !== dash) stack.push({ path: dash, label: 'Dashboard' });
  trailChanged();
}

export function backTarget(fallback) {
  return stack.length ? stack[stack.length - 1] : (fallback || null);
}

export function goBack(fallback) {
  const target = backTarget(fallback);
  if (!target) return;
  if (!mayLeave(target.path)) return;
  if (stack.length) { stack.pop(); trailChanged(); }
  backNavInFlight = true;
  backRestorePending = true;
  navigate(target.path);            // synchronously mounts the target — the screen reads wasBackNav()
  backRestorePending = false;       // only that one mount sees it; a later forward nav never does
  const y = lastScroll.get(target.path.split('?')[0]) || 0;   // scroll is tracked per path, not per query
  if (!y) return;
  // The target screen may still be growing after navigate() — async screens (the dashboard loads
  // its cards, charts and feeds after mount) aren't tall enough to reach `y` on the first frame,
  // so a single setScroll clamps short and the user lands back at the top. Retry across frames
  // until the scroll actually reaches `y` (content finally tall enough) or we give up (~600ms).
  let tries = 0;
  const restore = () => {
    setScroll(y);
    if (++tries < 12 && Math.abs(getScroll() - y) > 2) requestAnimationFrame(() => setTimeout(restore, 45));
  };
  restore();
}

export function clearNav() { backNavInFlight = false; stack = []; lastScroll.clear(); trailChanged(); }

/** iT's one ← button (nav.js backButton, S16 parity): labels itself with where you came from and
 *  returns there; `fallback` = { path, label } covers a deep link / reload with an empty trail.
 *  The label is read through the trail (watchTrail), not once at build time: the chip is built
 *  before the frame records the move into this screen. */
export function backChip(fallback) {
  return html`${() => {
    watchTrail();
    const target = backTarget(fallback);
    if (!target) return '';
    return html`<button type="button" class="nav-pill" @click=${() => goBack(fallback)}>
      ← ${target.label || (fallback && fallback.label) || 'Back'}
    </button>`;
  }}`;
}
